/* eslint-disable */
import { MEMORY_CONFIG } from '@/constants/memoryConfig'

export class RetirementManager {
  static RETIREMENT_REASONS = {
    AGE: 'Retired due to old age',
    PERFORMANCE: 'Retired due to poor performance',
    POPULATION: 'Sold to make room for younger chocobos',
    INJURY: 'Retired due to recurring injuries'
  }

  static calculateChocoboAge(chocobo, currentDay) {
    // Assume chocobos are "born" on day 1 if no birthDay is set
    const birthDay = chocobo.birthDay || 1
    const ageInDays = currentDay - birthDay
    const ageInYears = ageInDays / (32 * 12) // 32 days per month, 12 months per year
    return ageInYears
  }

  static isEligibleForRetirement(chocobo, currentDay) {
    const age = this.calculateChocoboAge(chocobo, currentDay)
    
    // Age-based retirement
    if (age >= MEMORY_CONFIG.retirementAgeYears) {
      return { eligible: true, reason: this.RETIREMENT_REASONS.AGE }
    }

    // Level-based retirement (old chocobos that haven't progressed)
    if (age >= 2 && chocobo.level < MEMORY_CONFIG.minRetirementLevel) {
      return { eligible: true, reason: this.RETIREMENT_REASONS.PERFORMANCE }
    }

    // Performance-based retirement (check recent race history)
    if (chocobo.raceHistory && chocobo.raceHistory.length >= 10) {
      const recentRaces = chocobo.raceHistory.slice(-10)
      const poorPerformances = recentRaces.filter(race => race.position >= 6).length
      
      if (poorPerformances >= 7) {
        return { eligible: true, reason: this.RETIREMENT_REASONS.PERFORMANCE }
      }
    }

    return { eligible: false, reason: null }
  }

  static evaluateOwnerChocobos(owner, chocobos, currentDay) {
    const ownerChocobos = chocobos.filter(c => owner.chocoboIds.includes(c.id))
    const retirementCandidates = []

    // Check each chocobo for retirement eligibility
    ownerChocobos.forEach(chocobo => {
      const { eligible, reason } = this.isEligibleForRetirement(chocobo, currentDay)
      if (eligible) {
        retirementCandidates.push({ chocobo, reason, priority: this.calculateRetirementPriority(chocobo) })
      }
    })

    // Sort by priority (higher priority = more likely to retire)
    retirementCandidates.sort((a, b) => b.priority - a.priority)

    return retirementCandidates
  }

  static calculateRetirementPriority(chocobo) {
    let priority = 0

    // Age factor
    const age = chocobo.level / 40 // Normalize to 0-1
    priority += age * 30

    // Poor performance factor
    if (chocobo.raceHistory && chocobo.raceHistory.length > 0) {
      const avgPosition = chocobo.raceHistory.reduce((sum, race) => sum + race.position, 0) / chocobo.raceHistory.length
      priority += avgPosition * 10
    }

    // Low stats factor
    const totalStats = chocobo.speed + chocobo.stamina
    priority += (300 - totalStats) / 3 // Lower stats = higher priority

    // Behavior factor (worse behaviors have higher priority)
    priority += chocobo.behaviorId * 5

    return priority
  }

  static shouldOwnerCullChocobos(owner, chocobos) {
    const ownerChocobos = chocobos.filter(c => owner.chocoboIds.includes(c.id))
    const maxAllowed = owner.isPlayer ? MEMORY_CONFIG.maxPlayerChocobos : MEMORY_CONFIG.maxNPCChocobos
    
    return ownerChocobos.length > maxAllowed
  }

  static selectChocobosForCulling(owner, chocobos, numberToCull) {
    const ownerChocobos = chocobos.filter(c => owner.chocoboIds.includes(c.id))
    
    // Score each chocobo (lower score = more likely to be culled)
    const scoredChocobos = ownerChocobos.map(chocobo => ({
      chocobo,
      score: this.calculateChocoboValue(chocobo)
    }))

    // Sort by score (ascending) and select the worst ones
    scoredChocobos.sort((a, b) => a.score - b.score)
    
    return scoredChocobos.slice(0, numberToCull).map(item => ({
      chocobo: item.chocobo,
      reason: this.RETIREMENT_REASONS.POPULATION
    }))
  }

  static calculateChocoboValue(chocobo) {
    let value = 0

    // Base stats value
    value += chocobo.speed * 2
    value += chocobo.stamina * 2

    // Level value
    value += chocobo.level * 50

    // X-factor bonus
    if (chocobo.hasSpeedXFactor) value += 1000
    if (chocobo.hasStaminaXFactor) value += 1000

    // Behavior value (better behaviors are worth more)
    value += (9 - chocobo.behaviorId) * 100

    // Recent performance
    if (chocobo.raceHistory && chocobo.raceHistory.length > 0) {
      const recentRaces = chocobo.raceHistory.slice(-5)
      const avgPosition = recentRaces.reduce((sum, race) => sum + race.position, 0) / recentRaces.length
      value += (9 - avgPosition) * 50
    }

    // Color rarity
    const colorValues = {
      yellow: 0,
      blue: 500,
      green: 500,
      black: 1000,
      pink: 1500,
      gold: 2500,
      white: 2000,
      red: 2000
    }
    value += colorValues[chocobo.color] || 0

    return value
  }

  static processRetirement(chocobo, owner, reason, gameStore) {
    const careerEarnings = this.calculateCareerEarnings(chocobo)
    const breedingStats = this.createBreedingStats(chocobo)
    const breedingAvailable = this.shouldMakeAvailableForBreeding(chocobo, careerEarnings)
    
    // Create enhanced retirement record
    const retirementRecord = {
      chocoboId: chocobo.id,
      chocoboName: chocobo.name,
      ownerId: owner.id,
      ownerName: owner.name,
      reason: reason,
      day: gameStore.currentDay,
      finalLevel: chocobo.level,
      careerEarnings: careerEarnings,
      totalRaces: chocobo.raceHistory ? chocobo.raceHistory.length : 0,
      
      // NEW: Breeding pool data
      breedingStats: breedingStats,
      breedingAvailable: breedingAvailable,
      breedingPrice: breedingAvailable ? this.calculateBreedingFee(breedingStats, careerEarnings) : 0,
      formerOwner: {
        id: owner.id,
        name: owner.name,
        isPlayer: owner.isPlayer || false
      }
    }

    // Remove chocobo from owner
    const chocoboIndex = owner.chocoboIds.indexOf(chocobo.id)
    if (chocoboIndex !== -1) {
      owner.chocoboIds.splice(chocoboIndex, 1)
    }

    // Remove chocobo from game
    const globalIndex = gameStore.chocobos.findIndex(c => c.id === chocobo.id)
    if (globalIndex !== -1) {
      gameStore.chocobos.splice(globalIndex, 1)
    }

    // Add to retirement history (with a cap)
    if (!gameStore.retirementHistory) {
      gameStore.retirementHistory = []
    }
    gameStore.retirementHistory.unshift(retirementRecord)
    if (gameStore.retirementHistory.length > 50) {
      gameStore.retirementHistory = gameStore.retirementHistory.slice(0, 50)
    }

    return retirementRecord
  }

  static calculateCareerEarnings(chocobo) {
    if (!chocobo.raceHistory) return 0
    
    // Estimate earnings based on race positions
    return chocobo.raceHistory.reduce((total, race) => {
      const positionEarnings = {
        1: 5000,
        2: 3000,
        3: 2000,
        4: 1000,
        5: 500,
        6: 250,
        7: 100,
        8: 50
      }
      return total + (positionEarnings[race.position] || 0)
    }, 0)
  }

  static calculateRaceStatistics(chocobo) {
    if (!chocobo.raceHistory || chocobo.raceHistory.length === 0) {
      return {
        totalRaces: 0,
        wins: 0,
        winRate: 0,
        avgPosition: 0,
        topThreeFinishes: 0
      }
    }

    const totalRaces = chocobo.raceHistory.length
    const wins = chocobo.raceHistory.filter(race => race.position === 1).length
    const topThreeFinishes = chocobo.raceHistory.filter(race => race.position <= 3).length
    const totalPositions = chocobo.raceHistory.reduce((sum, race) => sum + race.position, 0)
    const avgPosition = Math.round((totalPositions / totalRaces) * 10) / 10
    const winRate = Math.round((wins / totalRaces) * 100)

    return {
      totalRaces,
      wins,
      winRate,
      avgPosition,
      topThreeFinishes
    }
  }

  static createBreedingStats(chocobo) {
    const raceStats = this.calculateRaceStatistics(chocobo)
    
    // Import grade calculation (we'll need to import this properly)
    const getStatGrade = (value) => {
      const clampedValue = Math.min(200, Math.max(0, value))
      if (clampedValue >= 190) return 'S+'
      if (clampedValue >= 180) return 'S'
      if (clampedValue >= 170) return 'S-'
      if (clampedValue >= 150) return 'A+'
      if (clampedValue >= 125) return 'A'
      if (clampedValue >= 101) return 'A-'
      if (clampedValue >= 91) return 'B+'
      if (clampedValue >= 90) return 'B'
      if (clampedValue >= 80) return 'B-'
      if (clampedValue >= 70) return 'C+'
      if (clampedValue >= 60) return 'C'
      if (clampedValue >= 50) return 'C-'
      if (clampedValue >= 40) return 'D+'
      if (clampedValue >= 30) return 'D'
      if (clampedValue >= 20) return 'D-'
      if (clampedValue >= 10) return 'E'
      return 'F'
    }

    return {
      id: chocobo.id,
      name: chocobo.name,
      speed: chocobo.speed,
      stamina: chocobo.stamina,
      color: chocobo.color,
      region: chocobo.region,
      gender: chocobo.gender,
      level: chocobo.level,
      hasSpeedXFactor: chocobo.hasSpeedXFactor || false,
      hasStaminaXFactor: chocobo.hasStaminaXFactor || false,
      behaviorId: chocobo.behaviorId,
      gradeSpeed: getStatGrade(chocobo.speed),
      gradeStamina: getStatGrade(chocobo.stamina),
      ...raceStats
    }
  }

  static calculateBreedingFee(breedingStats, careerEarnings) {
    // Base fee starts at 1000 and increases with level
    let baseFee = 1000 + (breedingStats.level * 100)
    
    // Career earnings multiplier (max 2x)
    const careerMultiplier = Math.min(2.0, Math.max(1.0, careerEarnings / 50000))
    
    // Grade values for quality bonus
    const gradeValues = {
      'S+': 20, 'S': 18, 'S-': 16, 'A+': 14, 'A': 12, 'A-': 10,
      'B+': 8, 'B': 6, 'B-': 4, 'C+': 3, 'C': 2, 'C-': 1,
      'D+': 0, 'D': -1, 'D-': -2, 'E': -3, 'F': -4
    }
    
    const speedValue = gradeValues[breedingStats.gradeSpeed] || 0
    const staminaValue = gradeValues[breedingStats.gradeStamina] || 0
    const qualityBonus = Math.max(1.0, 1 + ((speedValue + staminaValue) / 40))
    
    // X-factor bonuses
    let xFactorBonus = 1.0
    if (breedingStats.hasSpeedXFactor) xFactorBonus += 0.5
    if (breedingStats.hasStaminaXFactor) xFactorBonus += 0.5
    
    // Color rarity multiplier
    const colorMultipliers = {
      'yellow': 1.0,
      'blue': 1.3,
      'green': 1.3,
      'pink': 1.4,
      'black': 1.5,
      'white': 1.8,
      'red': 1.8,
      'gold': 2.5
    }
    const colorMultiplier = colorMultipliers[breedingStats.color] || 1.0
    
    // Win rate bonus (up to 1000 extra)
    const winRateBonus = breedingStats.winRate * 10
    
    const finalFee = Math.round(
      (baseFee * careerMultiplier * qualityBonus * colorMultiplier * xFactorBonus) + winRateBonus
    )
    
    // Ensure minimum fee and reasonable maximum
    return Math.max(500, Math.min(50000, finalFee))
  }

  static shouldMakeAvailableForBreeding(chocobo, careerEarnings) {
    // Don't make chocobos below breeding level available (matches breeding validation)
    if (chocobo.level < 5) return false
    
    // Don't make very poor performers available
    if (chocobo.level < 10 && careerEarnings < 5000) return false
    
    // Always make high performers available
    if (chocobo.level >= 20 || careerEarnings >= 25000) return true
    if (chocobo.hasSpeedXFactor || chocobo.hasStaminaXFactor) return true
    
    // Color-based availability
    const valuableColors = ['black', 'gold', 'white', 'red', 'pink']
    if (valuableColors.includes(chocobo.color)) return true
    
    // Good stats make available
    if (chocobo.speed >= 120 || chocobo.stamina >= 120) return true
    
    // Random chance for others (30%)
    return Math.random() < 0.3
  }

  static processNPCRetirements(gameStore) {
    const retirements = []
    const currentDay = gameStore.currentDay

    gameStore.owners.forEach(owner => {
      if (owner.isPlayer) return // Skip player for automatic retirements

      // Check for mandatory retirements
      const candidates = this.evaluateOwnerChocobos(owner, gameStore.chocobos, currentDay)
      
      // Process up to 1 retirement per owner per day
      if (candidates.length > 0) {
        const toRetire = candidates[0]
        const record = this.processRetirement(toRetire.chocobo, owner, toRetire.reason, gameStore)
        retirements.push(record)
      }

      // Check if owner needs to cull chocobos due to population cap
      if (this.shouldOwnerCullChocobos(owner, gameStore.chocobos)) {
        const ownerChocobos = gameStore.chocobos.filter(c => owner.chocoboIds.includes(c.id))
        const maxAllowed = MEMORY_CONFIG.maxNPCChocobos
        const numberToCull = ownerChocobos.length - maxAllowed

        const cullCandidates = this.selectChocobosForCulling(owner, gameStore.chocobos, numberToCull)
        cullCandidates.forEach(({ chocobo, reason }) => {
          const record = this.processRetirement(chocobo, owner, reason, gameStore)
          retirements.push(record)
        })
      }
    })

    return retirements
  }

  static checkGlobalPopulationCap(gameStore) {
    if (gameStore.chocobos.length <= MEMORY_CONFIG.maxGlobalChocobos) {
      return []
    }

    const excessCount = gameStore.chocobos.length - MEMORY_CONFIG.maxGlobalChocobos
    const retirements = []

    // Find the worst performing chocobos - EXCLUDE player chocobos from consideration
    const allChocobos = [...gameStore.chocobos]
    const scoredChocobos = allChocobos
      .map(chocobo => ({
        chocobo,
        score: this.calculateChocoboValue(chocobo),
        owner: gameStore.owners.find(o => o.chocoboIds.includes(chocobo.id))
      }))
      .filter(({ owner }) => owner && !owner.isPlayer) // Filter out player chocobos entirely

    // Sort by score and remove the worst ones (only from NPC owners)
    scoredChocobos.sort((a, b) => a.score - b.score)
    const toRemove = scoredChocobos.slice(0, excessCount)

    toRemove.forEach(({ chocobo, owner }) => {
      // Double safety check (should always pass now due to filter above)
      if (owner && !owner.isPlayer) {
        const record = this.processRetirement(chocobo, owner, this.RETIREMENT_REASONS.POPULATION, gameStore)
        retirements.push(record)
      } else {
        console.error('CRITICAL: Attempted to retire player chocobo in population cap - blocked!', {
          chocobo: chocobo.name,
          owner: owner?.name,
          isPlayer: owner?.isPlayer
        })
      }
    })

    return retirements
  }
}